import type { GetPageResponse } from "@store/features/crm/pageV2Slice";
import type {
  KanbanSectionConfig,
  KanbanSubsectionConfig,
  KanbanExpandedSection,
  KanbanExpandedSubsection,
} from "./types";
import type { StructuredExpression } from "skedyul-ui/ui";
import {
  UNDEFINED_KANBAN_GROUP_COLOR,
  UNDEFINED_KANBAN_GROUPING_KEY,
  buildUndefinedKanbanSubQuery,
} from "skedyul-core/features/crm/features/pages/features/blocks/resources/types.kanbanKeys";
import { mergeSectionOptionsWithDefinition } from "skedyul-core/features/crm/features/pages/features/blocks/utils/mergeSectionOptionsWithDefinition";
import { canExpandKanbanGroup } from "skedyul-core/features/crm/features/pages/features/blocks/utils/kanbanGroupExpansion";

const resolveOptionColor = (
  pageData: GetPageResponse | undefined,
  fieldHandle: string,
  option: { value: string; label: string },
  fallback?: string,
): string | undefined => {
  const field = pageData?.fields?.find((f) => f.handle === fieldHandle);
  const defOption = field?.definition?.options?.find(
    (o) => o.value === option.value || o.label === option.label,
  );
  return defOption?.color ?? fallback ?? undefined;
};

const optionValuesFromMerged = (
  options: Array<{ value: string }>,
): string[] => options.map((option) => option.value);

const appendUndefinedEnumSection = (
  result: KanbanExpandedSection[],
  section: KanbanSectionConfig,
  optionValues: readonly string[],
): void => {
  result.push({
    field: section.field,
    groupingKey: UNDEFINED_KANBAN_GROUPING_KEY,
    color: UNDEFINED_KANBAN_GROUP_COLOR,
    // Core CRM filter type vs QueryBar UI type: same JSON shape, different aliases.
    subQuery: buildUndefinedKanbanSubQuery(
      section.field,
      optionValues,
    ) as StructuredExpression,
  });
};

const appendUndefinedEnumSubsection = (
  result: KanbanExpandedSubsection[],
  subsection: KanbanSubsectionConfig,
  optionValues: readonly string[],
): void => {
  result.push({
    field: subsection.field,
    groupingKey: UNDEFINED_KANBAN_GROUPING_KEY,
    // Core CRM filter type vs QueryBar UI type: same JSON shape, different aliases.
    subQuery: buildUndefinedKanbanSubQuery(
      subsection.field,
      optionValues,
    ) as StructuredExpression,
  });
};

export const expandGroupedSections = (
  sections: KanbanSectionConfig[],
  block: GetPageResponse["blocks"][number],
  pageData?: GetPageResponse,
): KanbanExpandedSection[] => {
  const result: KanbanExpandedSection[] = [];
  const fields = pageData?.fields ?? [];
  for (const s of sections) {
    if (
      s.field &&
      !canExpandKanbanGroup({
        group: s,
        fields,
        modelId: block.modelId,
      })
    ) {
      continue;
    }
    const key = (s.groupingKey || "").toLowerCase();

    // RELATION-based sections: when explicit options are present for this field,
    // generate one section per selected relation id with an equality filter.
    const relationField = pageData?.fields?.find(
      (f) =>
        f.modelId === block.modelId &&
        f.handle === s.field &&
        f.relationshipLink,
    );
    const optionsForField = Array.isArray(s.options) ? s.options : [];
    // RELATION sections: persisted options are the chosen relation set
    if (relationField && optionsForField.length) {
      optionsForField.forEach((option) => {
        const subQuery: StructuredExpression = {
          [s.field]: {
            eq: option.value,
          },
        };
        result.push({
          field: s.field,
          groupingKey: option.label,
          color: resolveOptionColor(pageData, s.field, option, s.color),
          subQuery,
        });
      });
      continue;
    }

    // Select/enum: persisted options are an order overlay on live definition
    if (
      key === "options" ||
      key === "option" ||
      key === "" ||
      optionsForField.length > 0
    ) {
      const field = pageData?.fields?.find((f) => f.handle === s.field);
      const definitionOptions = field?.definition?.options ?? [];
      if (definitionOptions.length > 0 || optionsForField.length > 0) {
        const merged = mergeSectionOptionsWithDefinition({
          persistedOptions: optionsForField,
          definitionOptions,
        });
        if (merged.length > 0) {
          merged.forEach((option) => {
            const subQuery: StructuredExpression = {
              [s.field]: {
                eq: option.value,
              },
            };
            result.push({
              field: s.field,
              groupingKey: option.label,
              color: option.color ?? s.color,
              subQuery,
            });
          });
          appendUndefinedEnumSection(result, s, optionValuesFromMerged(merged));
          continue;
        }
      }
    }

    if (key === "day_of_week" || key === "day-of-week") {
      const days = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ];
      days.forEach((label, index) => {
        // Generate day-specific filter (day_of_week is 1-based: Monday=1, Sunday=7)
        const dayOfWeekValue = index === 6 ? 7 : index + 1; // Sunday is 7, others are 1-6
        const subQuery: StructuredExpression = {
          [s.field]: {
            day_of_week: dayOfWeekValue,
          },
        };
        result.push({
          field: s.field,
          groupingKey: label,
          color: s.color,
          subQuery,
        });
      });
      continue;
    }
    if (key === "hour_of_day" || key === "hour-of-day") {
      const hours = Array.from({ length: 24 }, (_, i) => i);
      const formatHour = (h: number) => {
        const ampm = h < 12 ? "AM" : "PM";
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        return `${hour12} ${ampm}`;
      };
      hours.forEach((h) => {
        const subQuery: StructuredExpression = {
          [s.field]: {
            hour_of_day: h,
          },
        };
        result.push({
          field: s.field,
          groupingKey: formatHour(h),
          color: s.color,
          subQuery,
        });
      });
      continue;
    }
    if (key === "month_of_year" || key === "month-of-year") {
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      months.forEach((label, index) => {
        const subQuery: StructuredExpression = {
          [s.field]: {
            month_of_year: index + 1, // 1-based month numbering
          },
        };
        result.push({
          field: s.field,
          groupingKey: label,
          color: s.color,
          subQuery,
        });
      });
      continue;
    }
    if (!s.field) {
      result.push({ ...s, subQuery: undefined });
    }
  }
  return result;
};

export const expandGroupedSubsections = (
  subsections: KanbanSubsectionConfig[],
  pageData?: GetPageResponse,
): KanbanExpandedSubsection[] => {
  const result: KanbanExpandedSubsection[] = [];
  const fields = pageData?.fields ?? [];
  for (const s of subsections) {
    if (
      s.field &&
      !canExpandKanbanGroup({
        group: s,
        fields,
      })
    ) {
      continue;
    }
    const key = (s.groupingKey || "").toLowerCase();

    const customOptions = Array.isArray(s.options) ? s.options : [];
    if (
      key === "options" ||
      key === "option" ||
      key === "" ||
      customOptions.length > 0
    ) {
      const field = pageData?.fields?.find((f) => f.handle === s.field);
      const definitionOptions = field?.definition?.options ?? [];
      if (field?.relationshipLink && customOptions.length > 0) {
        customOptions.forEach((option) => {
          result.push({
            field: s.field,
            groupingKey: option.label,
            subQuery:
              s.subQuery ??
              ({
                [s.field]: {
                  eq: option.value,
                },
              } as StructuredExpression),
          });
        });
        continue;
      }
      if (definitionOptions.length > 0 || customOptions.length > 0) {
        const merged = mergeSectionOptionsWithDefinition({
          persistedOptions: customOptions,
          definitionOptions,
        });
        if (merged.length > 0) {
          merged.forEach((option) => {
            result.push({
              field: s.field,
              groupingKey: option.label,
              subQuery:
                s.subQuery ??
                ({
                  [s.field]: {
                    eq: option.value,
                  },
                } as StructuredExpression),
            });
          });
          appendUndefinedEnumSubsection(
            result,
            s,
            optionValuesFromMerged(merged),
          );
          continue;
        }
      }
    }

    if (key === "day_of_week" || key === "day-of-week") {
      const days = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ];
      days.forEach((label) => {
        result.push({
          field: s.field,
          groupingKey: label,
          subQuery: s.subQuery,
        });
      });
      continue;
    }
    if (key === "hour_of_day" || key === "hour-of-day") {
      const hours = Array.from({ length: 24 }, (_, i) => i);
      const formatHour = (h: number) => {
        const ampm = h < 12 ? "AM" : "PM";
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        return `${hour12} ${ampm}`;
      };
      hours.forEach((h) => {
        result.push({
          field: s.field,
          groupingKey: formatHour(h),
          subQuery: s.subQuery,
        });
      });
      continue;
    }
    if (key === "month_of_year" || key === "month-of-year") {
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      months.forEach((label) => {
        result.push({
          field: s.field,
          groupingKey: label,
          subQuery: s.subQuery,
        });
      });
      continue;
    }
    if (!s.field) {
      result.push(s);
    }
  }
  return result;
};
